import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, PencilSimple, Plus, Trash, Buildings, Receipt, Tray, Power, CurrencyGbp } from "@phosphor-icons/react";
import { toast } from "sonner";
import API from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { ModuleDialog } from "../../components/platform/bespoke/ModuleDialog";
import { AddTenantDialog } from "../../components/platform/bespoke/AddTenantDialog";
import { ChargesTable } from "../../components/platform/bespoke/ChargesTable";
import { RequestsBoard } from "../../components/platform/bespoke/RequestsBoard";
import { bespokeIcon, money, fmtDate, monthLabel, MODULE_STATUS, ROLE_LABELS } from "../../lib/bespoke";

function FeeDialog({ a, onClose, onDone }) {
  const [monthly, setMonthly] = useState(a.monthly_fee ?? "");
  const [note, setNote] = useState(a.note || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await API.put(`/platform/bespoke/assignments/${a.id}`, { monthly_fee: Number(monthly || 0), note }); toast.success("Updated"); onDone(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="bespoke-fee-dialog">
        <DialogHeader><DialogTitle className="font-[Chivo]">{a.tenant?.name}</DialogTitle><DialogDescription>Change the monthly fee from next month's charge onwards (this month's charge, if raised, is left as is).</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Monthly fee (£)</Label><Input type="number" min="0" step="0.01" value={monthly} onChange={(e) => setMonthly(e.target.value)} className="mt-1" data-testid="bespoke-fee-monthly" /></div>
          <div><Label className="text-xs">Note</Label><Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} className="mt-1" data-testid="bespoke-fee-note" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button><Button onClick={save} disabled={busy} data-testid="bespoke-fee-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExtraChargeDialog({ a, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await API.post(`/platform/bespoke/assignments/${a.id}/charges`, { amount: Number(amount || 0), description }); toast.success("Charge added"); onDone(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add the charge"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="bespoke-extra-charge-dialog">
        <DialogHeader><DialogTitle className="font-[Chivo]">Add a one-off charge</DialogTitle><DialogDescription>{a.tenant?.name} — e.g. a change request or extra training. Raised as due today, 14-day terms.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Amount (£) *</Label><Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1" data-testid="bespoke-extra-amount" /></div>
          <div><Label className="text-xs">What for</Label><Input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} placeholder="e.g. Extra report added — agreed 12 Nov" className="mt-1" data-testid="bespoke-extra-description" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button><Button onClick={save} disabled={busy || !amount} data-testid="bespoke-extra-save">Add charge</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignmentRow({ a, onChanged }) {
  const [fee, setFee] = useState(false);
  const [extra, setExtra] = useState(false);
  const end = async () => {
    const note = window.prompt(`End ${a.module_key} for ${a.tenant?.name}? No more monthly charges are raised; due charges stay on the ledger. Optional note:`, "");
    if (note === null) return;
    try { await API.post(`/platform/bespoke/assignments/${a.id}/end`, { note }); toast.success("Module ended for this tenant"); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || "Could not end"); }
  };
  const active = a.status === "active";
  return (
    <div className={`rounded-xl border border-border bg-card p-4 ${active ? "" : "opacity-70"}`} data-testid={`bespoke-assignment-${a.id}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold">{a.tenant?.name}</h3>
            {a.tenant?.slug && <Link to={`/platform/tenants?q=${a.tenant.slug}`} className="text-[10px] text-muted-foreground hover:underline">/{a.tenant.slug}</Link>}
            <Badge className={`text-[9px] ${active ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" : "bg-zinc-200 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"}`} data-testid={`bespoke-assignment-status-${a.id}`}>{active ? "Switched on" : "Ended"}</Badge>
            {!a.tenant?.exists && <Badge variant="outline" className="text-[9px]">workspace deleted</Badge>}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Since {fmtDate(a.started_at)}{a.ended_at ? ` · ended ${fmtDate(a.ended_at)}` : ""} · set-up {money(a.one_off_fee)} · <b>{money(a.monthly_fee)}/month</b>{a.monthly_fee > 0 ? ` from ${monthLabel(a.monthly_from)}` : ""}
          </p>
          <p className="text-xs mt-1" data-testid={`bespoke-assignment-totals-${a.id}`}>
            <span className={a.totals?.due > 0 ? "text-amber-700 dark:text-amber-300 font-medium" : "text-muted-foreground"}>{money(a.totals?.due)} due{a.totals?.overdue > 0 ? ` (${money(a.totals.overdue)} overdue)` : ""}</span>
            <span className="text-muted-foreground"> · {money(a.totals?.paid)} paid</span>
          </p>
          {(a.note || a.end_note) && <p className="text-[11px] text-muted-foreground mt-1 italic">{[a.note, a.end_note].filter(Boolean).join(" · ")}</p>}
        </div>
        {active && (
          <div className="flex gap-1">
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setExtra(true)} data-testid={`bespoke-assignment-extra-${a.id}`}><CurrencyGbp size={12} className="mr-1" /> Add charge</Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setFee(true)} data-testid={`bespoke-assignment-fee-${a.id}`}><PencilSimple size={12} className="mr-1" /> Fee</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600" onClick={end} data-testid={`bespoke-assignment-end-${a.id}`}><Power size={12} className="mr-1" /> End</Button>
          </div>
        )}
      </div>
      {fee && <FeeDialog a={a} onClose={() => setFee(false)} onDone={onChanged} />}
      {extra && <ExtraChargeDialog a={a} onClose={() => setExtra(false)} onDone={onChanged} />}
    </div>
  );
}

export default function BespokeModuleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [edit, setEdit] = useState(false);
  const [add, setAdd] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await API.get(`/platform/bespoke/modules/${id}`); setM(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Module not found"); navigate("/platform/bespoke"); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    if (!window.confirm(`Delete '${m.name}'? Only possible while no tenant has ever had it.`)) return;
    try { await API.delete(`/platform/bespoke/modules/${m.id}`); toast.success("Module deleted"); navigate("/platform/bespoke"); } catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  if (!m) return <div className="p-6 text-center text-muted-foreground" data-testid="bespoke-detail-loading">Loading…</div>;
  const Icon = bespokeIcon(m.icon);
  const st = MODULE_STATUS[m.status] || MODULE_STATUS.draft;
  const active = m.assignments.filter((a) => a.status === "active");

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="bespoke-detail-page">
      <Link to="/platform/bespoke" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="bespoke-detail-back"><ArrowLeft size={12} /> All bespoke modules</Link>
      <div className="flex flex-wrap items-start gap-4">
        <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0"><Icon size={30} weight="duotone" className="text-primary" /></div>
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold font-[Chivo]" data-testid="bespoke-detail-name">{m.name}</h1>
            <Badge className={`text-[9px] ${st.cls}`} data-testid="bespoke-detail-status">{st.label}</Badge>
          </div>
          <p className="text-xs font-mono text-muted-foreground">/modules/{m.key} · API /api/bespoke/{m.key}/…</p>
          {m.description && <p className="text-sm text-muted-foreground mt-2 max-w-2xl" data-testid="bespoke-detail-description">{m.description}</p>}
          <p className="text-xs text-muted-foreground mt-2">Defaults: <b>{money(m.one_off_fee)}</b> set-up · <b>{money(m.monthly_fee)}</b>/month · visible to {m.roles.map((r) => ROLE_LABELS[r] || r).join(", ")}</p>
          {m.notes && <details className="mt-2 text-xs"><summary className="cursor-pointer text-muted-foreground">Internal notes</summary><p className="mt-1 whitespace-pre-wrap rounded-md bg-muted p-3" data-testid="bespoke-detail-notes">{m.notes}</p></details>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setEdit(true)} data-testid="bespoke-detail-edit-btn"><PencilSimple size={14} className="mr-1" /> Edit</Button>
          {m.assignments.length === 0 && <Button variant="ghost" size="sm" className="text-red-600" onClick={remove} data-testid="bespoke-detail-delete-btn"><Trash size={14} className="mr-1" /> Delete</Button>}
          <Button size="sm" onClick={() => setAdd(true)} disabled={m.status === "retired"} data-testid="bespoke-detail-add-tenant-btn"><Plus size={14} className="mr-1" /> Add to tenant</Button>
        </div>
      </div>

      {m.status === "draft" && active.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 px-4 py-3 text-xs text-amber-900 dark:text-amber-200" data-testid="bespoke-detail-draft-note">
          {active.length} tenant{active.length === 1 ? " has" : "s have"} this module while it's still being built — it appears in their menu the moment you set the status to <b>Live</b>.
        </div>
      )}

      <Tabs defaultValue="tenants">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="tenants" data-testid="bespoke-detail-tab-tenants"><Buildings size={14} className="mr-1" /> Tenants ({active.length})</TabsTrigger>
          <TabsTrigger value="charges" data-testid="bespoke-detail-tab-charges"><Receipt size={14} className="mr-1" /> Charges{m.due > 0 ? ` (${money(m.due)} due)` : ""}</TabsTrigger>
          <TabsTrigger value="requests" data-testid="bespoke-detail-tab-requests"><Tray size={14} className="mr-1" /> Requests ({m.requests.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="tenants" className="mt-4 space-y-3">
          {m.assignments.length === 0 && (
            <div className="rounded-xl border border-dashed border-border p-10 text-center" data-testid="bespoke-detail-tenants-empty">
              <p className="font-semibold">Not on any tenant yet</p>
              <p className="text-sm text-muted-foreground mt-1">Add it to the customer who asked — the set-up fee is raised straight away and the monthly fee follows on the 1st.</p>
              <Button size="sm" className="mt-4" onClick={() => setAdd(true)} data-testid="bespoke-detail-empty-add-btn"><Plus size={14} className="mr-1" /> Add to tenant</Button>
            </div>
          )}
          {m.assignments.map((a) => <AssignmentRow key={a.id} a={a} onChanged={load} />)}
        </TabsContent>
        <TabsContent value="charges" className="mt-4"><ChargesTable charges={m.charges} onChanged={load} showModule={false} /></TabsContent>
        <TabsContent value="requests" className="mt-4"><RequestsBoard requests={m.requests} onChanged={load} onBuild={() => toast.info("This request is already linked to this module")} /></TabsContent>
      </Tabs>

      <ModuleDialog open={edit} onOpenChange={setEdit} module={m} roles={m.role_options} onSaved={load} />
      <AddTenantDialog open={add} onOpenChange={setAdd} module={m} onAdded={load} />
    </div>
  );
}
